import { ActionReducerMap } from '@ngrx/store';
import { AuthWithMfaDetails } from '@public/store/models/auth.model';
import { authReducer } from '@public/store/reducers/auth.reducer';
import { BridgeInterfaceConfigurationReducer } from '@secure/devices/store/models/bridge-interface-configuration.model';
import { DeviceConfigurationReducer } from '@secure/devices/store/models/device-configuration.model';
import { DNATConfigurationReducer } from '@secure/devices/store/models/dnat.model';
import { HardwareInterfaceConfigurationReducer } from '@secure/devices/store/models/hardware-interface-configuration.model';
import { QoSConfigurationReducer } from '@secure/devices/store/models/qos-configuration.model';
import { RemoteSSHConfigurationReducer } from '@secure/devices/store/models/remote-ssh.model';
import { SplitDnsConfigurationReducer } from '@secure/devices/store/models/split-dns.model';
import { StaticRoutingDetailConfigurationReducer } from '@secure/devices/store/models/static-routing-detail.model';
import { StaticRoutingConfigurationReducer } from '@secure/devices/store/models/static-routing.model';
import { TrafficSteeringConfigurationReducer } from '@secure/devices/store/models/traffic-steering-configuration.model';
import { UsbInterfaceConfigurationReducer } from '@secure/devices/store/models/usb-interface-configuration.model';
import { VlanInterfaceConfigurationReducer } from '@secure/devices/store/models/vlan-interface-configuration.model';
import {
  VpnRoutingConfigurationReducer,
  VpnRoutingServerConfigurationReducer,
} from '@secure/devices/store/models/vpn-routing.model';
import { WirelessInterfaceConfigurationReducer } from '@secure/devices/store/models/wireless-interface-configuration.model';
import { bridgeInterfaceConfigurationReducer } from '@secure/devices/store/reducers/bridge-interface-configuration.reducer';
import { deviceConfigurationReducer } from '@secure/devices/store/reducers/device-configuration.reducer';
import { devicesReducer } from '@secure/devices/store/reducers/devices.reducer';
import { hardwareInterfaceConfigurationReducer } from '@secure/devices/store/reducers/hardware-interface-configuration.reducer';
import { qosConfigurationReducer } from '@secure/devices/store/reducers/qos-configuration.reducer';
import { splitDnsConfigurationReducer } from '@secure/devices/store/reducers/split-dns-configuration.reducer';
import { staticRoutingConfigurationReducer } from '@secure/devices/store/reducers/static-routing-configuration.reducer';
import { staticRoutingDetailConfigurationReducer } from '@secure/devices/store/reducers/static-routing-detail-configuration.reducer';
import { trafficSteeringReducer } from '@secure/devices/store/reducers/traffic-steering-configuration.reducer';
import { tunnelInterfaceConfigurationReducer } from '@secure/devices/store/reducers/tunnel-interface-configuration.reducer';
import { usbInterfaceConfigurationReducer } from '@secure/devices/store/reducers/usb-interface-configuration.reducer';
import { vlanInterfaceConfigurationReducer } from '@secure/devices/store/reducers/vlan-interface-configuration.reducer';
import { vpnRoutingConfigurationReducer } from '@secure/devices/store/reducers/vpn-routing-configuration.reducer';
import { vpnRoutingServerConfigurationReducer } from '@secure/devices/store/reducers/vpn-routing-server-configuration.reducer';
import { wirelessInterfaceConfigurationReducer } from '@secure/devices/store/reducers/wireless-interface-configuration.reducer';
import { organizationsReducer } from '@secure/organizations/store/reducers/organizations.reducers';
import { usersReducer } from '@secure/users/store/reducers/users.reducer';
import {
  DynamicRoutingReducer,
  DynamicRoutingStatistics,
} from '@views/secure/devices/configuration/routing/dynamic-routing/dynamic-routing.model';
import { dynamicRoutingReducer } from '@views/secure/devices/configuration/routing/dynamic-routing/store/dynamic-routing.reducer';
import { dynamicRoutingStatisticsReducer } from '@views/secure/devices/configuration/routing/dynamic-routing/statistics/store/dynamic-routing-statistics.reducer';
import { PolicyRoutingStatistics } from '@views/secure/devices/configuration/routing/policy-routing-statistics/policy-routing-statistics.model';
import { policyRoutingStatisticsReducer } from '@views/secure/devices/configuration/routing/policy-routing-statistics/store/policy-routing-statistics.reducer';
import { IpsecCertificateState } from '@views/secure/devices/configuration/ipsec/certificate/store/ipsec-certificate.interface';
import { ipsecCertificateReducer } from '@views/secure/devices/configuration/ipsec/certificate/store/ipsec-certificate.reducer';
import { IpsecClientState } from '@views/secure/devices/configuration/ipsec/client/store/ipsec-client.interface';
import { ipsecClientReducer } from '@views/secure/devices/configuration/ipsec/client/store/ipsec-client.reducer';
import { IpsecStatisticsState } from '@views/secure/devices/configuration/ipsec/ipsec-statistics/store/ipsec-statistics.interface';
import { ipsecStatisticsReducer } from '@views/secure/devices/configuration/ipsec/ipsec-statistics/store/ipsec-statistics.reducer';
import { IpsecServerState } from '@views/secure/devices/configuration/ipsec/server/store/ipsec-server.interface';
import { ipsecServerReducer } from '@views/secure/devices/configuration/ipsec/server/store/ipsec-server.reducer';
import { CellularInterfaceReducer } from '@views/secure/devices/configuration/networking-interface/cellular-interface/cellular-interface.model';
import { cellularInterfacesReducer } from '@views/secure/devices/configuration/networking-interface/cellular-interface/store/cellular-interface.reducer';
import { trafficStatisticsReducer } from '@views/secure/devices/configuration/networking-interface/traffic-statistics/store/traffic-statistics.reducer';
import { TrafficStatisticsReducer } from '@views/secure/devices/configuration/networking-interface/traffic-statistics/traffic-statistics.model';
import { suricataReducer } from '@views/secure/devices/configuration/suricata/store/suricata.reducer';
import { SuricataReducer } from '@views/secure/devices/configuration/suricata/suricata.model';
import { TrafficSteeringStateStatisticsState } from '@views/secure/devices/configuration/traffic-steering/traffic-steering-state-statistics/store/traffic-steering-state-statistics.model';
import { trafficSteeringStateStatisticsReducer } from '@views/secure/devices/configuration/traffic-steering/traffic-steering-state-statistics/store/traffic-steering-state-statistics.reducer';
import { tsForwardSettingsReducer } from '@views/secure/devices/configuration/traffic-steering/ts-forward-settings/store/ts-forward-settings.reducer';
import { TsForwardSettingsReducer } from '@views/secure/devices/configuration/traffic-steering/ts-forward-settings/ts-forward-settings.model';
import { userGroupsReducer } from '@views/secure/devices/configuration/traffic-steering/user-groups/store/user-groups.reducer';
import { UserGroupsReducer } from '@views/secure/devices/configuration/traffic-steering/user-groups/user-groups.model';
import { bypassListReducer } from '@views/secure/devices/configuration/traffic-steering/bypass-list/store/bypass-list.reducer';
import { BypassListReducer } from '@views/secure/devices/configuration/traffic-steering/bypass-list/bypass-list.model';
import { ActiveHostsReducer } from '@views/secure/devices/store/models/active-hosts.model';
import { IpsecInterfaceReducer } from '@views/secure/devices/store/models/ipsec-interface.model';
import { MacIpBindingReducer } from '@views/secure/devices/store/models/mac-ip-binding.model';
import { PrismaInterfaceReducer } from '@views/secure/devices/store/models/prisma-interface.model';
import { DynamicDns } from '@views/secure/devices/store/models/dynamic-dns.model';
import { SNMPConfiguration } from '@views/secure/devices/store/models/snmp.model';
import { StaticHostsConfiguration } from '@views/secure/devices/store/models/static-hosts.model';
import { TrafficSteeringStatisticsReducer } from '@views/secure/devices/store/models/traffic-steering-statistics.model';
import { TunnelInterfaceConfigurationReducer } from '@views/secure/devices/store/models/tunnel-interface-configuration.model';
import { activeHostsReducer } from '@views/secure/devices/store/reducers/active-hosts.reducer';
import { dnatConfigurationReducer } from '@views/secure/devices/store/reducers/dnat-configuration.reducer';
import { ipsecInterfaceReducer } from '@views/secure/devices/store/reducers/ipsec-interface.reducer';
import { macIPBindingReducer } from '@views/secure/devices/store/reducers/mac-ip-binding.reducer';
import { prismaInterfaceReducer } from '@views/secure/devices/store/reducers/prisma-interface.reducer';
import { remoteSSHConfigurationReducer } from '@views/secure/devices/store/reducers/remote-ssh-configuration.reducer';
import { dynamicDnsReducer } from '@views/secure/devices/store/reducers/dynamic-dns.reducer';
import { snmpReducer } from '@views/secure/devices/store/reducers/snmp.reducer';
import { staticHostsReducer } from '@views/secure/devices/store/reducers/static-hosts.reducer';
import { trafficSteeringStatisticsReducer } from '@views/secure/devices/store/reducers/traffic-steering-statistics.reducer';
import { NotificationsReducer } from '@views/secure/notifications/store/notifications.model';
import { notificationsReducer } from '@views/secure/notifications/store/notifications.reducer';
import { OrganizationsReducer } from '@views/secure/organizations/store/models/organizations.model';
import { ApplicationDomainState } from '@views/secure/settings/application-domains/application-domains.model';
import { customDomainsReducer } from '@views/secure/settings/application-domains/custom-domains/store/custom-domains.reducer';
import { defaultDomainsReducer } from '@views/secure/settings/application-domains/default-domains/store/default-domains.reducer';
import { DeviceModelState } from '@views/secure/settings/device-model/store/device-model.model';
import { deviceModelReducer } from '@views/secure/settings/device-model/store/device-model.reducer';
import { LogForwardingReducer } from '@views/secure/settings/log-forwarding/log-forwarding.model';
import { logForwardingReducer } from '@views/secure/settings/log-forwarding/store/log-forwarding.reducer';
import { PrismaServersReducer } from '@views/secure/settings/prisma-servers/store/prisma-servers.model';
import { prismaServersReducer } from '@views/secure/settings/prisma-servers/store/prisma-servers.reducer';
import { SoftwareState } from '@views/secure/softwares/store/models/software.model';
import { softwaresReducer } from '@views/secure/softwares/store/reducers/softwares.reducers';
import { StatusSummaryReducer } from '@views/secure/status-summary/status-summary.model';
import { statusSummaryReducer } from '@views/secure/status-summary/store/status-summary.reducer';
import { UserReducer } from '@views/secure/users/store/models/users.model';
import { settingsReducer } from '@views/secure/settings/store/settings.reducer';
import { SettingsReducer } from '@views/secure/settings/store/settings.model';

export interface StoreState {
  devices: any;
  users: UserReducer;
  deviceConfiguration: DeviceConfigurationReducer;
  trafficSteeringConfiguration: TrafficSteeringConfigurationReducer;
  trafficSteeringStatistics: TrafficSteeringStatisticsReducer;
  trafficSteeringStateStatistics: TrafficSteeringStateStatisticsState;
  qosConfiguration: QoSConfigurationReducer;
  hardwareInterfaceConfiguration: HardwareInterfaceConfigurationReducer;
  wirelessInterfaceConfiguration: WirelessInterfaceConfigurationReducer;
  usbInterfaceConfiguration: UsbInterfaceConfigurationReducer;
  tunnelInterfaceConfiguration: TunnelInterfaceConfigurationReducer;
  vlanInterfaceConfiguration: VlanInterfaceConfigurationReducer;
  bridgeInterfaceConfiguration: BridgeInterfaceConfigurationReducer;
  splitDnsConfiguration: SplitDnsConfigurationReducer;
  vpnRoutingConfiguration: VpnRoutingConfigurationReducer;
  vpnRoutingServerConfiguration: VpnRoutingServerConfigurationReducer;
  auth: AuthWithMfaDetails;
  organizations: OrganizationsReducer;
  softwares: SoftwareState;
  staticRoutingConfiguration: StaticRoutingConfigurationReducer;
  staticRoutingDetailConfiguration: StaticRoutingDetailConfigurationReducer;
  dnatConfiguration: DNATConfigurationReducer;
  remoteSSHConfiguration: RemoteSSHConfigurationReducer;
  ipsecServer: IpsecServerState;
  ipsecClient: IpsecClientState;
  ipsecCertificate: IpsecCertificateState;
  ipsecStatistics: IpsecStatisticsState;
  prismaServers: PrismaServersReducer;
  notifications: NotificationsReducer;
  ipsecInterface: IpsecInterfaceReducer;
  prismaInterface: PrismaInterfaceReducer;
  defaultDomains: ApplicationDomainState;
  customDomains: ApplicationDomainState;
  activeHosts: ActiveHostsReducer;
  macIpBinding: MacIpBindingReducer;
  deviceModel: DeviceModelState;
  snmp: SNMPConfiguration;
  dynamicDns: DynamicDns;
  staticHosts: StaticHostsConfiguration;
  trafficStatistics: TrafficStatisticsReducer;
  userGroups: UserGroupsReducer;
  tsForwardSettings: TsForwardSettingsReducer;
  statusSummary: StatusSummaryReducer;
  cellularInterfaces: CellularInterfaceReducer;
  logForwarding: LogForwardingReducer;
  suricata: SuricataReducer;
  bypassList: BypassListReducer;
  dynamicRouting: DynamicRoutingReducer;
  dynamicRoutingStatistics: DynamicRoutingStatistics;
  policyRoutingStatistics: PolicyRoutingStatistics[];
  settings: SettingsReducer;
}

export const reducers: ActionReducerMap<StoreState, any> = {
  devices: devicesReducer,
  users: usersReducer,
  deviceConfiguration: deviceConfigurationReducer,
  trafficSteeringConfiguration: trafficSteeringReducer,
  trafficSteeringStatistics: trafficSteeringStatisticsReducer,
  trafficSteeringStateStatistics: trafficSteeringStateStatisticsReducer,
  qosConfiguration: qosConfigurationReducer,
  hardwareInterfaceConfiguration: hardwareInterfaceConfigurationReducer,
  wirelessInterfaceConfiguration: wirelessInterfaceConfigurationReducer,
  usbInterfaceConfiguration: usbInterfaceConfigurationReducer,
  tunnelInterfaceConfiguration: tunnelInterfaceConfigurationReducer,
  vlanInterfaceConfiguration: vlanInterfaceConfigurationReducer,
  bridgeInterfaceConfiguration: bridgeInterfaceConfigurationReducer,
  splitDnsConfiguration: splitDnsConfigurationReducer,
  vpnRoutingConfiguration: vpnRoutingConfigurationReducer,
  vpnRoutingServerConfiguration: vpnRoutingServerConfigurationReducer,
  auth: authReducer,
  organizations: organizationsReducer,
  softwares: softwaresReducer,
  staticRoutingConfiguration: staticRoutingConfigurationReducer,
  staticRoutingDetailConfiguration: staticRoutingDetailConfigurationReducer,
  dnatConfiguration: dnatConfigurationReducer,
  remoteSSHConfiguration: remoteSSHConfigurationReducer,
  ipsecServer: ipsecServerReducer,
  ipsecClient: ipsecClientReducer,
  ipsecCertificate: ipsecCertificateReducer,
  ipsecStatistics: ipsecStatisticsReducer,
  prismaServers: prismaServersReducer,
  notifications: notificationsReducer,
  ipsecInterface: ipsecInterfaceReducer,
  prismaInterface: prismaInterfaceReducer,
  defaultDomains: defaultDomainsReducer,
  customDomains: customDomainsReducer,
  activeHosts: activeHostsReducer,
  macIpBinding: macIPBindingReducer,
  deviceModel: deviceModelReducer,
  snmp: snmpReducer,
  dynamicDns: dynamicDnsReducer,
  staticHosts: staticHostsReducer,
  trafficStatistics: trafficStatisticsReducer,
  userGroups: userGroupsReducer,
  tsForwardSettings: tsForwardSettingsReducer,
  statusSummary: statusSummaryReducer,
  cellularInterfaces: cellularInterfacesReducer,
  logForwarding: logForwardingReducer,
  suricata: suricataReducer,
  bypassList: bypassListReducer,
  dynamicRouting: dynamicRoutingReducer,
  dynamicRoutingStatistics: dynamicRoutingStatisticsReducer,
  policyRoutingStatistics: policyRoutingStatisticsReducer,
  settings: settingsReducer,
};
